import React from 'react';
import { DatePicker } from '@roger/ui-components';
import { parseISOToLocalDate } from '../../../utils/dateUtils';
import { Question } from '../types';

/** Format a Date as YYYYMMDD (e.g. "20260316") for OASIS validation. */
const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/** Parse a YYYYMMDD string (e.g. "20260316") into a local Date. */
const parseYYYYMMDD = (str: string): Date | undefined => {
  if (!/^\d{8}$/.test(str)) return undefined;
  const year = parseInt(str.slice(0, 4), 10);
  const month = parseInt(str.slice(4, 6), 10);
  const day = parseInt(str.slice(6, 8), 10);
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? undefined : date;
};

interface DateQuestionProps {
  question: Question;
  value: Date | string;
  onChange: (value: Date | string) => void;
}

export const DateQuestion: React.FC<DateQuestionProps> = ({
  question,
  value,
  onChange,
}) => {
  // Convert string to Date if needed (supports YYYYMMDD and YYYY-MM-DD)
  const dateValue =
    value instanceof Date
      ? value
      : value
        ? (parseYYYYMMDD(String(value)) ?? parseISOToLocalDate(String(value)))
        : undefined;

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      onChange(formatDateToYYYYMMDD(date));
    } else {
      onChange('');
    }
  };

  return (
    <div className='space-y-2'>
      <label className='text-sm font-semibold text-foreground block'>
        {question.question || question.description}
      </label>

      <DatePicker
        date={dateValue}
        onDateChange={handleDateChange}
        placeholder='Select date...'
        datePickerSize='default'
        className='w-full'
      />
    </div>
  );
};
